/**
 * `<QuestionList>` — Questions section's collapsible list (Batch 30j-4).
 *
 * Spec: `frontend/docs/30j_4_design.md` §3.5 +
 *       `frontend/docs/30j_4_architect.md` §5.3.
 *
 * Each question renders as a numbered collapsible. Default-collapsed —
 * recruiter scans questions first, expands the ones they want context on.
 * Follow-up renders through `<EvaluationMarkdown>` (HARD-2 — no fork).
 *
 * The `border-l-2 border-section-recommendation/40` accent on the expanded
 * follow-up is HARD-9-compliant: it's a BORDER, not body text.
 *
 * Phase 2.6.2 S3 (§4.4): Bare `<ChevronDown>` replaced with a contentful
 * pill showing follow-up count context:
 *   - q.followUp.length > 0 → "N follow-ups"   (actually followUp is
 *     string | null per PrepQuestion, not an array — treat non-null as
 *     "1 follow-up" per the single-string parser contract)
 *   - q.followUp === null  → "Add a follow-up to context"
 * The pill is only rendered when there is a follow-up affordance
 * (q.followUp is defined, i.e. not undefined). Since PrepQuestion always
 * has followUp: string | null, the pill always renders — the two states
 * are "has content" vs "none yet".
 */
import { useState, type JSX } from "react";

import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";
import type { PrepQuestion } from "@/lib/parsePrep";

/**
 * S3 (§4.4): Compute the contentful pill label for a question row.
 * `followUp` is `string | null` per the ParsedPrep contract (single
 * follow-up body per question from the parser).
 */
function followUpPillLabel(followUp: string | null): string {
  return followUp !== null ? "1 follow-up" : "Add a follow-up to context";
}

interface QuestionListProps {
  questions: PrepQuestion[];
}

function classNames(
  ...parts: Array<string | false | null | undefined>
): string {
  return parts.filter(Boolean).join(" ");
}

export function QuestionList({ questions }: QuestionListProps): JSX.Element {
  const [openSet, setOpenSet] = useState<Set<number>>(new Set());

  const toggle = (n: number): void => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  return (
    <ol className="space-y-2.5" data-testid="prep-questions">
      {questions.map((q) => {
        const isOpen = openSet.has(q.number);
        return (
          <li
            key={q.number}
            className="rounded-md border border-border-default bg-surface-subtle px-3.5 py-2.5"
            data-testid={`prep-question-${q.number}`}
          >
            <button
              type="button"
              onClick={() => toggle(q.number)}
              className="flex w-full items-start gap-2 text-left"
              aria-expanded={isOpen}
              data-testid={`prep-question-toggle-${q.number}`}
            >
              <span className="text-2xs font-bold tabular-nums text-text-tertiary mt-0.5">
                {q.number}.
              </span>
              <span className="flex-1 text-sm font-medium text-text-primary">
                {q.question}
              </span>
              {/* S3 (§4.4): Contentful pill replaces bare chevron */}
              <span
                className={classNames(
                  "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset transition-colors",
                  q.followUp !== null
                    ? "bg-status-in-process-bg text-status-in-process-fg ring-status-in-process-ring"
                    : "bg-surface-subtle text-text-tertiary ring-border-default",
                )}
                data-testid={`prep-question-followup-pill-${q.number}`}
              >
                {followUpPillLabel(q.followUp)}
              </span>
            </button>
            {isOpen && q.followUp ? (
              <div
                className="mt-2.5 pl-5 border-l-2 border-section-recommendation/40"
                data-testid={`prep-question-followup-${q.number}`}
              >
                <EvaluationMarkdown source={q.followUp} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
